import { type ReactElement } from 'react';
import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Typography, Descriptions, Tag, Card, Table, Empty, Grid } from 'antd';

import type { AdminCustomerDetail, AdminCustomerOrder } from '@/types';

const { Title } = Typography;
const { useBreakpoint } = Grid;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Render a JSONB object as a pretty-printed code block */
function JsonSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown> | null;
}): ReactElement | null {
  if (!data) return null;

  return (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <pre
        style={{
          maxHeight: 400,
          overflow: 'auto',
          fontSize: 12,
          background: '#f5f5f5',
          padding: 12,
          borderRadius: 6,
          margin: 0,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </Card>
  );
}

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomerShow(): ReactElement {
  const { query } = useShow<AdminCustomerDetail>();
  const record = query.data?.data;
  const isLoading = query.isLoading;

  const screens = useBreakpoint();
  const descColumns = screens.md ? 2 : 1;

  return (
    <Show isLoading={isLoading}>
      {record && (
        <>
          {/* Contact info */}
          <Title level={5}>Contact Information</Title>
          <Card style={{ marginBottom: 16 }}>
            <Descriptions bordered column={descColumns} size="small">
              <Descriptions.Item label="Email">
                {record.email}
              </Descriptions.Item>
              <Descriptions.Item label="Name">
                {record.name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {record.phone ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {record.country ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Marketing Opt-in">
                {record.marketingOptIn ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Joined">
                {formatDate(record.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {formatDate(record.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Consent versions */}
          <Title level={5}>Consent Versions</Title>
          <Card style={{ marginBottom: 16 }}>
            <Descriptions bordered column={descColumns} size="small">
              <Descriptions.Item label="ToS Version">
                {record.tosAcceptedVersion ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="ToS Accepted At">
                {formatDate(record.tosAcceptedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Privacy Policy Version">
                {record.privacyPolicyAcceptedVersion ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Privacy Policy Accepted At">
                {formatDate(record.privacyPolicyAcceptedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Biometric Consent Version">
                {record.biometricConsentVersion ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Biometric Consent At">
                {formatDate(record.biometricConsentAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Biometric Notice Hash" span={2}>
                {record.biometricConsentNoticeHash ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Addresses */}
          <Title level={5}>Addresses</Title>
          <JsonSection title="Billing Address" data={record.billingAddress} />
          <JsonSection title="Shipping Address" data={record.shippingAddress} />

          {/* Order history */}
          <Title level={5} style={{ marginTop: 16 }}>
            Order History
          </Title>
          <Card>
            {record.customerOrders && record.customerOrders.length > 0 ? (
              <Table<AdminCustomerOrder>
                dataSource={record.customerOrders}
                rowKey="id"
                size="small"
                pagination={false}
              >
                <Table.Column<AdminCustomerOrder>
                  dataIndex="orderId"
                  title="Order ID"
                  render={(orderId: string) => (
                    <a href={`/orders/show/${orderId}`}>{orderId}</a>
                  )}
                  ellipsis
                  width={200}
                />
                <Table.Column<AdminCustomerOrder>
                  dataIndex="orderDate"
                  title="Order Date"
                  render={(value: string) => formatDate(value)}
                  width={160}
                />
                <Table.Column<AdminCustomerOrder>
                  dataIndex="productType"
                  title="Product Type"
                  render={(value: string | null) => value ?? '—'}
                  width={120}
                />
                <Table.Column<AdminCustomerOrder>
                  dataIndex="amountPaid"
                  title="Amount"
                  render={(value: number | null) => formatCents(value)}
                  width={100}
                  align="right"
                />
                <Table.Column<AdminCustomerOrder>
                  dataIndex="currency"
                  title="Currency"
                  width={90}
                />
              </Table>
            ) : (
              <Empty description="No orders" />
            )}
          </Card>
        </>
      )}
    </Show>
  );
}
